import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
  useWatch,
} from 'react-hook-form';
import { ReportType } from '../../../../../types';
import { ReportFormValues } from '../reports-form-page';
import { ClearIcon, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import StepDateRange from './step-date-range';
import StepDateSelection from './step-date-selection';
import StepExternalEmailAddress from './step-external-email';
import { SectionHeader } from '../../../../shared/section-header';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface StepConfigurationProps {
  control: Control<ReportFormValues>;
  selectedReportType: ReportType | null;
  errors: FieldErrors<ReportFormValues>;
  watch: UseFormWatch<ReportFormValues>;
  clearErrors: UseFormClearErrors<ReportFormValues>;
  setValue: UseFormSetValue<ReportFormValues>;
  hasDateRange: boolean;
  hasDateSelection: boolean;
}

export default function StepConfiguration({
  control,
  selectedReportType,
  errors,
  watch,
  hasDateRange,
  hasDateSelection,
  clearErrors,
  setValue,
}: StepConfigurationProps) {
  const regexTypes = ['SSN', 'Credit Card', 'Phone Numbers', 'Custom'];
  const regexTypeSelectedList = useWatch({
    control,
    name: 'regexTypeSelectedList',
  });
  const isCustomSelected =
    Array.isArray(regexTypeSelectedList) &&
    regexTypeSelectedList.includes('Custom');

  const reportType: string | undefined = selectedReportType?.name;
  const inactiveSinceDate = watch('inactiveSinceDate');
  const stepConfigurationHeader = (reportType: string | undefined): string => {
    switch (reportType) {
      case 'docsModOverXdays':
        return 'Please provide the number of days back from today that you would like to report on.';
      case 'inactiveUsers':
        return 'Define criteria for identifying inactive users.';
      default:
        return 'Please select which sources you would like to report on.';
    }
  };

  const stepConfigurationBody = (reportType: string | undefined) => {
    if (hasDateRange) {
      return (
        <StepDateRange
          control={control}
          watch={watch}
          errors={errors}
          selectedReportType={selectedReportType}
          clearErrors={clearErrors}
          setValue={setValue}
        />
      );
    } else if (hasDateSelection) {
      return <StepDateSelection control={control} />;
    }

    switch (reportType) {
      case 'docsModOverXdays':
        return (
          <Controller
            name="daysback"
            control={control}
            render={({ field }) => (
              <TextField
                required
                type="number"
                {...field}
                inputProps={{
                  id: 'daysback',
                  min: 0,
                }}
                InputLabelProps={{
                  htmlFor: 'daysback',
                }}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(
                    value === '' || value === null ? null : Number(value)
                  );
                }}
                label="Days Back"
                sx={{ width: { xs: '100%', lg: '50%' } }}
                error={!!errors.daysback}
                helperText={errors?.daysback?.message || ' '}
              />
            )}
          />
        );

      case 'inactiveUsers':
        return (
          <>
            <Stack alignItems={'left'} gap={2}>
              <Stack flexDirection={'column'} gap={2} flexWrap="wrap">
                <Controller
                  name="neverLoggedInCheck"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Include users who have never logged in"
                    />
                  )}
                />
                <Controller
                  name="inactiveSinceDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Inactive Since"
                      value={
                        inactiveSinceDate ? dayjs(inactiveSinceDate) : null
                      }
                      onChange={(date) => {
                        if (!date || !isNaN(date.valueOf())) {
                          field.onChange(dayjs(date).valueOf());
                        }
                      }}
                      slotProps={{
                        textField: {
                          error: !!errors.inactiveSinceDate,
                          helperText:
                            errors.inactiveSinceDate?.message ??
                            'Show users who have not logged in since this date',
                          InputProps: inactiveSinceDate
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => field.onChange(null)}
                                      edge="end"
                                      size="small"
                                      aria-label="clear date"
                                    >
                                      <ClearIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }
                            : {},
                        },
                      }}
                      sx={{ width: { xs: '100%', lg: '30%' } }}
                    />
                  )}
                />
              </Stack>
            </Stack>
          </>
        );
      case 'externallyOwnedDocs':
        return <StepExternalEmailAddress control={control} errors={errors} />;
      default:
        return (
          <Stack alignItems={'left'} gap={2}>
            <Stack flexDirection={'row'} gap={2} flexWrap="wrap">
              <Controller
                control={control}
                name="regexTypeSelectedList"
                render={({ field: { value, onChange } }) => {
                  return (
                    <>
                      {regexTypes.map((type) => (
                        <FormControlLabel
                          key={type}
                          control={
                            <Checkbox
                              inputProps={{
                                id: 'regexTypeSelectedList',
                              }}
                              value={type}
                              checked={value?.includes(type)}
                              onChange={(event) => {
                                const newValue = event.target.checked
                                  ? value
                                    ? [...value, type]
                                    : [type]
                                  : value?.filter((item) => item !== type);
                                onChange(newValue);
                              }}
                            />
                          }
                          label={type}
                        />
                      ))}
                    </>
                  );
                }}
              />
            </Stack>
            {errors.regexTypeSelectedList && (
              <Box width="fit-content">
                <Grow in={true}>
                  <Alert severity="error">
                    {errors.regexTypeSelectedList.message}
                  </Alert>
                </Grow>
              </Box>
            )}
            {isCustomSelected && (
              <Controller
                name="regex"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Custom Regex"
                    variant="outlined"
                    {...field}
                    error={!!errors.regex}
                    helperText={errors?.regex?.message || ' '}
                    inputProps={{
                      id: 'customRegex',
                    }}
                    InputLabelProps={{
                      htmlFor: 'customRegex',
                    }}
                    sx={{ width: { xs: '100%', lg: '50%' } }}
                  />
                )}
              />
            )}
          </Stack>
        );
    }
  };

  return (
    <Stack gap={2} width={'100%'}>
      <Stack alignItems={'left'}>
        {!hasDateRange &&
          !hasDateSelection &&
          reportType !== 'externallyOwnedDocs' && (
            <Stack gap={2} width={'100%'} alignItems={'left'}>
              <SectionHeader>Configuration</SectionHeader>

              <Typography sx={{ mb: 1 }}>
                {stepConfigurationHeader(reportType)}
              </Typography>
            </Stack>
          )}
        {stepConfigurationBody(reportType)}
      </Stack>
    </Stack>
  );
}
